// ============================================================================
// GroupDetailPage - one group: chat, games, members
// ============================================================================
// The rally page. Members get three tabs:
//   Chat    - group chat served by the API (polled every few seconds while
//             open; owner and moderators can delete messages)
//   Games   - "playing now" invites and scheduled games; schedule one and
//             pick specific members to invite, or invite everyone
//   Members - the roster with roles; owner promotes moderators, owner and
//             moderators remove members and answer join requests
// Outsiders see a join button (public) or a request-to-join button plus the
// invite-code path (private).
// ============================================================================
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Box, Container, Heading, Text, VStack, HStack, Spinner, Button, Input,
  Textarea, Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody,
  ModalCloseButton, Select, useToast, Avatar, AvatarGroup, Checkbox, IconButton, Badge,
  Menu, MenuButton, MenuList, MenuItem,
} from '@chakra-ui/react';
import { Globe, Lock, Copy, CalendarPlus, Play, Bell, BellOff, Send, Trash2, MessageCircle, Users, Calendar, Shield, ShieldOff, UserMinus, Check, X, UserPlus, Pencil, Link2, Share2, ImagePlus, MoreHorizontal, Flag, Ban } from 'lucide-react';
import { gameApiService, GroupMemberCard, GroupChatMessage } from '../services/gameApiService';
import enhancedTableService from '../services/enhancedTableService';
import { auth } from '../firebase';
import GroupCover, { GROUP_COVER_THEMES } from '../components/groups/GroupCover';
import { uploadGroupCover } from '../services/groupCoverService';
import { getFriends, searchUsers } from '../services/socialService';
import blockService, { useBlockedUsers } from '../services/blockService';
import { submitReport } from '../services/reportService';

const CARD_BG = '#0f1722';
const CHAT_POLL_MS = 4000;

interface GroupEvent {
  id: string; type: 'now' | 'scheduled'; status: string;
  tableId: string | null; tableName: string | null; privateCode: string | null;
  tableConfig: { currency: string; smallBlind: number; bigBlind: number; maxSeats: number } | null;
  scheduledFor: number | null; note: string;
  createdBy: string; createdByName: string; registered: string[]; invited: string[];
  createdAt: number | null;
}

type Tab = 'chat' | 'games' | 'members';

const fmtWhen = (ms: number) => new Date(ms).toLocaleString('en-US', {
  weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
});
const fmtTime = (ms: number) => {
  const d = new Date(ms);
  const today = new Date().toDateString() === d.toDateString();
  return today ? d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
    : d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};

const GroupDetailPage = () => {
  const { groupId } = useParams<{ groupId: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const myUid = auth.currentUser?.uid || '';

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [group, setGroup] = useState<any>(null);
  const [members, setMembers] = useState<GroupMemberCard[]>([]);
  const [membersNext, setMembersNext] = useState<number | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [pending, setPending] = useState<Array<{ uid: string; name: string; photoURL: string | null }>>([]);
  const [events, setEvents] = useState<GroupEvent[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [codeCopied, setCodeCopied] = useState(false);
  const [tab, setTab] = useState<Tab>('games');
  const [joinCodeInput, setJoinCodeInput] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();

  // Invite modal
  const [showInvite, setShowInvite] = useState(false);
  const [friends, setFriends] = useState<Array<{ uid: string; name: string; photoURL?: string }>>([]);
  const [friendsLoading, setFriendsLoading] = useState(false);
  const [inviteSearch, setInviteSearch] = useState('');
  const [inviteResults, setInviteResults] = useState<Array<{ uid: string; name: string; photoURL?: string }>>([]);
  const [invitePicked, setInvitePicked] = useState<Set<string>>(new Set());
  const [inviting, setInviting] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  // Edit modal (owner / moderators)
  const [showEdit, setShowEdit] = useState(false);
  const [editName, setEditName] = useState('');
  const [editDesc, setEditDesc] = useState('');
  const [editTheme, setEditTheme] = useState(0);
  const [editCoverUrl, setEditCoverUrl] = useState<string | null>(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);

  // @mentions
  const [mentionQuery, setMentionQuery] = useState<string | null>(null);
  const chatInputRef = useRef<HTMLInputElement | null>(null);

  // Players I blocked: their messages are hidden for me
  const blockedUsers = useBlockedUsers(myUid || null);
  const reportMessage = async (m: GroupChatMessage) => {
    if (!window.confirm(`Report ${m.senderName}'s message to the moderators?`)) return;
    const r = await submitReport(m.senderId, 'harassment', `Group chat (${group?.name || groupId}): "${m.text.slice(0, 300)}"`);
    toast({ title: r.success ? 'Reported' : 'Could not report', description: r.message, status: r.success ? 'success' : 'error', duration: 4000 });
  };
  const blockSender = async (m: GroupChatMessage) => {
    if (!window.confirm(`Block ${m.senderName}? You will not see their messages, and they cannot message you.`)) return;
    try { await blockService.blockUser(m.senderId); toast({ title: `${m.senderName} blocked`, status: 'success', duration: 3000 }); }
    catch (e: any) { toast({ title: e?.message || 'Could not block', status: 'error', duration: 3000 }); }
  };

  // Schedule modal
  const [showSchedule, setShowSchedule] = useState(false);
  const [schedWhen, setSchedWhen] = useState('');
  const [schedNote, setSchedNote] = useState('');
  const [schedCurrency, setSchedCurrency] = useState<'playcoins' | 'pkcash'>('playcoins');
  const [schedSB, setSchedSB] = useState(50);
  const [schedBB, setSchedBB] = useState(100);
  const [schedSeats, setSchedSeats] = useState(8);
  const [scheduling, setScheduling] = useState(false);
  const [inviteEveryone, setInviteEveryone] = useState(true);
  const [invitees, setInvitees] = useState<Set<string>>(new Set());
  const [inviteFilter, setInviteFilter] = useState('');

  // Chat
  const [messages, setMessages] = useState<GroupChatMessage[]>([]);
  const [chatLoading, setChatLoading] = useState(false);
  const [chatHasMore, setChatHasMore] = useState(false);
  const [chatText, setChatText] = useState('');
  const [sending, setSending] = useState(false);
  const chatBoxRef = useRef<HTMLDivElement | null>(null);
  const messagesRef = useRef<GroupChatMessage[]>([]);
  messagesRef.current = messages;

  const isMember: boolean = !!group?.isMember;
  const canModerate: boolean = !!(group?.isOwner || group?.isModerator);

  const load = useCallback(async () => {
    if (!groupId) return;
    const r = await gameApiService.getGroupDetail(groupId);
    if (r.success) {
      setGroup(r.group);
      setMembers(r.members || []);
      setMembersNext((r.members || []).length < (r.group?.memberCount || 0) ? (r.members || []).length : null);
      setPending((r as any).pendingRequests || []);
      setEvents((r.events || []) as GroupEvent[]);
      setError(null);
    } else {
      setError(r.error || 'Could not load this group');
    }
    setLoading(false);
  }, [groupId]);
  useEffect(() => { load(); }, [load]);

  // Open on the chat tab when arriving from an unread dot
  useEffect(() => {
    if (group?.isMember && group?.chat?.unread) setTab('chat');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [group?.id]);

  // Arrived through a link: ?invite=<id> (personal invite), ?code=XXXXXX
  // (private group code) or ?join=1 (public share link). Join straight
  // away, then clean the URL.
  useEffect(() => {
    if (!group || !groupId || group.isMember) return;
    const inviteId = searchParams.get('invite');
    const code = searchParams.get('code');
    const join = searchParams.get('join');
    if (!inviteId && !code && !join) return;
    setSearchParams({});
    (async () => {
      setBusy('join');
      const r = await gameApiService.joinGroup(inviteId ? { groupId, inviteId } : code ? { code } : { groupId });
      setBusy(null);
      if (r.success) {
        if (r.requested) toast({ title: 'Request sent', description: 'The owner will accept or decline it.', status: 'info', duration: 4000 });
        else toast({ title: `Welcome to ${group.name}`, status: 'success', duration: 3000 });
        load();
      } else toast({ title: r.error || 'Could not join', status: 'error', duration: 4000 });
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [group?.id, group?.isMember]);

  // ── Invite ──
  const inviteLink = (() => {
    const base = `${window.location.origin}/groups/${groupId}`;
    if (!group) return base;
    return group.joinCode ? `${base}?code=${group.joinCode}` : `${base}?join=1`;
  })();
  const openInvite = async () => {
    setShowInvite(true);
    setInvitePicked(new Set());
    setInviteSearch('');
    setInviteResults([]);
    if (!myUid || friends.length) return;
    setFriendsLoading(true);
    try {
      const list = await getFriends(myUid);
      setFriends(list.map((f: any) => ({ uid: f.userId || f.uid, name: f.username || f.displayName || 'Player', photoURL: f.photoURL || f.avatarUrl })));
    } finally { setFriendsLoading(false); }
  };
  const runInviteSearch = async (q: string) => {
    setInviteSearch(q);
    if (q.trim().length < 2) { setInviteResults([]); return; }
    const list = await searchUsers(q.trim(), myUid, 10).catch(() => []);
    setInviteResults(list.map((f: any) => ({ uid: f.userId || f.uid, name: f.username || f.displayName || 'Player', photoURL: f.photoURL || f.avatarUrl })));
  };
  const toggleInvitePick = (uid: string) => setInvitePicked(prev => { const n = new Set(prev); if (n.has(uid)) n.delete(uid); else n.add(uid); return n; });
  const sendInvites = async () => {
    if (!groupId || invitePicked.size === 0) return;
    setInviting(true);
    const r = await gameApiService.groupInvite(groupId, Array.from(invitePicked));
    setInviting(false);
    if (r.success) {
      toast({ title: `${r.invited || 0} invite${(r.invited || 0) === 1 ? '' : 's'} sent`, description: (r.alreadyIn || 0) > 0 ? `${r.alreadyIn} already in the group.` : 'They get a notification and an invite in Messages.', status: 'success', duration: 4000 });
      setInvitePicked(new Set());
    } else toast({ title: r.error || 'Could not send invites', status: 'error', duration: 4000 });
  };
  const copyLink = () => {
    navigator.clipboard?.writeText(inviteLink).then(() => { setLinkCopied(true); setTimeout(() => setLinkCopied(false), 2000); });
  };
  const shareLink = async () => {
    const nav: any = navigator;
    if (nav.share) {
      try { await nav.share({ title: group?.name, text: `Join ${group?.name} on PokerSweepStars`, url: inviteLink }); } catch { /* cancelled */ }
    } else copyLink();
  };

  // ── Edit ──
  const openEdit = () => {
    if (!group) return;
    setEditName(group.name || '');
    setEditDesc(group.description || '');
    setEditTheme(Number(group.coverTheme) || 0);
    setEditCoverUrl(group.coverUrl || null);
    setShowEdit(true);
  };
  const saveEdit = async () => {
    if (!groupId) return;
    setSavingEdit(true);
    const patch: any = { description: editDesc.trim() };
    if (group.isOwner && editName.trim() !== group.name) patch.name = editName.trim();
    if (editCoverUrl && editCoverUrl !== group.coverUrl) patch.coverUrl = editCoverUrl;
    else if (!editCoverUrl) patch.coverTheme = editTheme;
    const r = await gameApiService.updateGroup(groupId, patch);
    setSavingEdit(false);
    if (r.success) { setShowEdit(false); load(); }
    else toast({ title: r.error || 'Could not save', status: 'error', duration: 4000 });
  };
  const handleCoverFile = async (file: File | null) => {
    if (!file || !groupId || !myUid) return;
    setUploadingCover(true);
    try {
      const url = await uploadGroupCover(myUid, groupId, file);
      setEditCoverUrl(url);
    } catch (e: any) {
      toast({ title: e?.message || 'Upload failed', status: 'error', duration: 4000 });
    } finally { setUploadingCover(false); }
  };

  // ── @mentions ──
  const mentionable = members.filter(m => m.uid !== myUid);
  const mentionCandidates = mentionQuery === null ? [] : mentionable.filter(m => m.name.toLowerCase().startsWith(mentionQuery.toLowerCase())).slice(0, 6);
  const onChatInput = (value: string) => {
    setChatText(value);
    const m = /(?:^|\s)@([^\s@]{0,30})$/.exec(value);
    setMentionQuery(m ? m[1] : null);
  };
  const pickMention = (name: string) => {
    setChatText(prev => prev.replace(/(?:^|\s)@([^\s@]{0,30})$/, (full) => `${full.startsWith(' ') ? ' ' : ''}@${name} `));
    setMentionQuery(null);
    chatInputRef.current?.focus();
  };
  const mentionsIn = (text: string): string[] => mentionable.filter(m => text.includes(`@${m.name}`)).map(m => m.uid);
  const renderWithMentions = (text: string) => {
    const names = members.map(m => m.name).filter(Boolean).sort((a, b) => b.length - a.length);
    if (names.length === 0) return text;
    const re = new RegExp(`@(${names.map(n => n.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})`, 'g');
    const parts: Array<string | { name: string }> = [];
    let last = 0; let mm: RegExpExecArray | null;
    while ((mm = re.exec(text)) !== null) {
      if (mm.index > last) parts.push(text.slice(last, mm.index));
      parts.push({ name: mm[1] });
      last = mm.index + mm[0].length;
    }
    if (last < text.length) parts.push(text.slice(last));
    return parts.map((p, i) => typeof p === 'string' ? p : <Text as="span" key={i} color="#22d3ee" fontWeight="700">@{p.name}</Text>);
  };

  // ── Join / request ──
  const handleJoin = async (code?: string) => {
    if (!groupId) return;
    setBusy('join');
    const r = await gameApiService.joinGroup(code ? { code } : { groupId });
    setBusy(null);
    if (r.success) {
      if ((r as any).requested) toast({ title: 'Request sent', description: 'The owner or a moderator will accept or decline it. We will let you know.', status: 'info', duration: 5000 });
      load();
    } else toast({ title: r.error || 'Could not join', status: 'error', duration: 4000 });
  };
  const handleCancelRequest = async () => {
    if (!groupId) return;
    const r = await gameApiService.cancelGroupRequest(groupId);
    if (r.success) load(); else toast({ title: r.error || 'Failed', status: 'error', duration: 3000 });
  };
  const handleRequestAction = async (uid: string, action: 'accept' | 'decline') => {
    if (!groupId) return;
    setBusy(`req-${uid}`);
    const r = await gameApiService.groupRequestAction(groupId, uid, action);
    setBusy(null);
    if (r.success) load(); else toast({ title: r.error || 'Failed', status: 'error', duration: 3000 });
  };

  const handleLeave = async () => {
    if (!groupId || !window.confirm('Leave this group?')) return;
    const r = await gameApiService.leaveGroup(groupId);
    if (r.success) navigate('/groups');
    else toast({ title: r.error || 'Could not leave', status: 'error', duration: 4000 });
  };

  const handleDelete = async () => {
    if (!groupId || !window.confirm('Delete this group for everyone? This cannot be undone.')) return;
    const r = await gameApiService.deleteGroup(groupId);
    if (r.success) navigate('/groups');
    else toast({ title: r.error || 'Could not delete', status: 'error', duration: 4000 });
  };

  // ── Members ──
  const loadMoreMembers = async () => {
    if (!groupId || membersNext === null) return;
    setLoadingMore(true);
    const r = await gameApiService.groupMembers(groupId, membersNext, 50);
    setLoadingMore(false);
    if (r.success) {
      setMembers(prev => [...prev, ...(r.members || []).filter(m => !prev.some(p => p.uid === m.uid))]);
      setMembersNext(r.nextOffset ?? null);
    }
  };
  const handleMemberAction = async (m: GroupMemberCard, action: 'remove' | 'promote' | 'demote') => {
    if (!groupId) return;
    const msg = action === 'remove' ? `Remove ${m.name} from the group?` : action === 'promote' ? `Make ${m.name} a moderator? They can approve requests, remove members and delete chat messages.` : `Remove ${m.name}'s moderator role?`;
    if (!window.confirm(msg)) return;
    setBusy(`mem-${m.uid}`);
    const r = await gameApiService.groupMemberAction(groupId, m.uid, action);
    setBusy(null);
    if (r.success) load(); else toast({ title: r.error || 'Failed', status: 'error', duration: 3500 });
  };

  // ── Events ──
  const handleRegister = async (ev: GroupEvent, register: boolean) => {
    setBusy(ev.id);
    const r = await gameApiService.registerGroupEvent(ev.id, register);
    setBusy(null);
    if (r.success) load();
    else toast({ title: r.error || 'Failed', status: 'error', duration: 3000 });
  };

  const handleCancelEvent = async (ev: GroupEvent) => {
    if (!window.confirm('Cancel this game for the group?')) return;
    const r = await gameApiService.cancelGroupEvent(ev.id);
    if (r.success) load();
    else toast({ title: r.error || 'Failed', status: 'error', duration: 3000 });
  };

  // Creator starts a scheduled game: create the table through the normal
  // flow (all its validations apply), then attach it to the event - the
  // server pushes "game is live" to everyone registered.
  const handleStartEvent = async (ev: GroupEvent) => {
    if (!myUid || !ev.tableConfig) return;
    setBusy(ev.id);
    try {
      const cfg = ev.tableConfig;
      const created = await enhancedTableService.createEnhancedTable({
        ownerId: myUid,
        currency: (cfg.currency === 'pkcash' ? 'pkcash' : 'playcoins') as 'playcoins' | 'pkcash',
        smallBlind: cfg.smallBlind,
        bigBlind: cfg.bigBlind,
        maxSeats: cfg.maxSeats,
        customName: `${group?.name || 'Group'} Game`,
        isPrivate: true,
        tableTheme: 'premium',
      } as any);
      if (!created.success || !created.tableId) {
        toast({ title: created.error || 'Could not create the table', status: 'error', duration: 4000 });
        return;
      }
      const r = await gameApiService.startGroupEvent(ev.id, created.tableId);
      if (!r.success) {
        toast({ title: r.error || 'Table made, but the group was not notified', status: 'warning', duration: 4000 });
      }
      navigate(`/game/${created.tableId}`);
    } finally {
      setBusy(null);
    }
  };

  const openSchedule = () => {
    setInviteEveryone(true);
    setInvitees(new Set());
    setInviteFilter('');
    setShowSchedule(true);
    if (membersNext !== null && members.length < 200) loadMoreMembers();
  };
  const toggleInvitee = (uid: string) => {
    setInvitees(prev => { const n = new Set(prev); if (n.has(uid)) n.delete(uid); else n.add(uid); return n; });
  };
  const handleSchedule = async () => {
    if (!groupId) return;
    const when = schedWhen ? new Date(schedWhen).getTime() : NaN;
    if (!isFinite(when) || when < Date.now() + 5 * 60 * 1000) {
      toast({ title: 'Pick a time at least 5 minutes from now', status: 'warning', duration: 3500 });
      return;
    }
    if (!inviteEveryone && invitees.size === 0) {
      toast({ title: 'Pick at least one member to invite, or invite everyone', status: 'warning', duration: 3500 });
      return;
    }
    setScheduling(true);
    const r = await gameApiService.scheduleGroupGame(groupId, when, schedNote.trim(), {
      currency: schedCurrency, smallBlind: schedSB, bigBlind: schedBB, maxSeats: schedSeats,
    }, inviteEveryone ? undefined : Array.from(invitees));
    setScheduling(false);
    if (r.success) {
      setShowSchedule(false);
      setSchedNote('');
      toast({ title: inviteEveryone ? 'Game scheduled - the group has been notified' : `Game scheduled - ${r.invited || invitees.size} member${(r.invited || invitees.size) === 1 ? '' : 's'} invited`, status: 'success', duration: 3500 });
      load();
    } else {
      toast({ title: r.error || 'Could not schedule', status: 'error', duration: 4000 });
    }
  };

  const copyCode = () => {
    if (!group?.joinCode) return;
    navigator.clipboard?.writeText(group.joinCode).then(() => {
      setCodeCopied(true);
      setTimeout(() => setCodeCopied(false), 2000);
    });
  };

  // ── Chat: load latest, then poll for newer while the tab is open ──
  const scrollChatToBottom = () => {
    const el = chatBoxRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  };
  const loadChat = useCallback(async () => {
    if (!groupId) return;
    setChatLoading(true);
    const r = await gameApiService.groupChatMessages(groupId);
    setChatLoading(false);
    if (r.success) {
      setMessages(r.messages || []);
      setChatHasMore(!!r.hasMore);
      setTimeout(scrollChatToBottom, 0);
      gameApiService.groupChatMarkRead(groupId).catch(() => {});
    }
  }, [groupId]);
  const pollChat = useCallback(async () => {
    if (!groupId || document.hidden) return;
    const cur = messagesRef.current;
    // Overlap the window by 2s: two writes landing out of order on different
    // server instances would otherwise let one slip past; ids are deduped below.
    const last = cur.length ? cur[cur.length - 1].createdAt : 0;
    const r = await gameApiService.groupChatMessages(groupId, last ? { after: Math.max(0, last - 2000) } : {});
    if (!r.success || !r.messages?.length) return;
    const el = chatBoxRef.current;
    const nearBottom = !el || el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    setMessages(prev => {
      const seen = new Set(prev.map(m => m.id));
      const fresh = (r.messages || []).filter(m => !seen.has(m.id));
      return fresh.length ? [...prev, ...fresh] : prev;
    });
    if (nearBottom) setTimeout(scrollChatToBottom, 0);
    gameApiService.groupChatMarkRead(groupId).catch(() => {});
  }, [groupId]);
  useEffect(() => {
    if (!isMember || tab !== 'chat') return;
    if (membersNext !== null && members.length < 200) loadMoreMembers();
    loadChat();
    const t = setInterval(pollChat, CHAT_POLL_MS);
    const onVis = () => { if (!document.hidden) pollChat(); };
    document.addEventListener('visibilitychange', onVis);
    return () => { clearInterval(t); document.removeEventListener('visibilitychange', onVis); };
  }, [isMember, tab, loadChat, pollChat]);

  const loadOlder = async () => {
    if (!groupId || !messages.length) return;
    const r = await gameApiService.groupChatMessages(groupId, { before: messages[0].createdAt });
    if (r.success) {
      setMessages(prev => [...(r.messages || []), ...prev]);
      setChatHasMore(!!r.hasMore);
    }
  };
  const handleSend = async () => {
    const text = chatText.trim();
    if (!groupId || !text || sending) return;
    setSending(true);
    const r = await gameApiService.groupChatSend(groupId, text, mentionsIn(text));
    setSending(false);
    if (r.success && r.message) {
      setChatText('');
      setMentionQuery(null);
      setMessages(prev => prev.some(m => m.id === r.message!.id) ? prev : [...prev, r.message!]);
      setTimeout(scrollChatToBottom, 0);
    } else {
      toast({ title: r.error || 'Could not send', status: 'error', duration: 3500 });
    }
  };
  const handleDeleteMessage = async (m: GroupChatMessage) => {
    if (!groupId || !window.confirm('Delete this message?')) return;
    const r = await gameApiService.groupChatDelete(groupId, m.id);
    if (r.success) setMessages(prev => prev.map(x => x.id === m.id ? { ...x, deleted: true, text: '' } : x));
    else toast({ title: r.error || 'Failed', status: 'error', duration: 3000 });
  };

  if (loading) return <Box minH="100vh" bg="#0a1420" pt={24} textAlign="center"><Spinner color="cyan.400" size="lg" /></Box>;
  if (error || !group) return (
    <Box minH="100vh" bg="#0a1420" pt={16}>
      <Container maxW="container.md" textAlign="center">
        <Text color="red.300" mb={4}>{error || 'Group not found'}</Text>
        <Button size="sm" variant="outline" colorScheme="cyan" onClick={() => navigate('/groups')}>Back to groups</Button>
      </Container>
    </Box>
  );

  const openScheduled = events.filter(e => e.type === 'scheduled' && e.status === 'open');
  const liveNow = events.filter(e => (e.type === 'now' || e.status === 'started') && e.status !== 'cancelled' && e.status !== 'expired' && e.tableId);
  const history = events.filter(e => !openScheduled.includes(e) && !liveNow.includes(e));
  const memberFilterLower = inviteFilter.trim().toLowerCase();
  const pickable = members.filter(m => m.uid !== myUid && (!memberFilterLower || m.name.toLowerCase().includes(memberFilterLower)));

  const roleTag = (role: string | null) => role === 'owner'
    ? <Badge colorScheme="yellow" fontSize="9px">OWNER</Badge>
    : role === 'moderator' ? <Badge colorScheme="purple" fontSize="9px">MOD</Badge> : null;

  const TabButton = ({ id, icon, label, dot }: { id: Tab; icon: ReactNode; label: string; dot?: boolean }) => (
    <Button size="sm" flex={1} variant={tab === id ? 'solid' : 'outline'} colorScheme="purple"
      color={tab === id ? 'white' : 'gray.200'} borderColor={tab === id ? 'transparent' : 'whiteAlpha.300'}
      bg={tab === id ? undefined : 'whiteAlpha.50'} _hover={{ bg: tab === id ? undefined : 'whiteAlpha.200' }}
      leftIcon={icon as any} onClick={() => setTab(id)} position="relative">
      {label}
      {dot && <Box position="absolute" top="6px" right="8px" w="7px" h="7px" borderRadius="full" bg="#22d3ee" />}
    </Button>
  );

  return (
    <Box minH="100vh" bg="#0a1420" pb={16}>
      <Container maxW="container.md" pt={6}>
        <GroupCover coverUrl={group.coverUrl} coverTheme={group.coverTheme} name={group.name} height={{ base: 160, md: 200 } as any} radius="18px">
          <Box position="absolute" top="10px" left="12px" right="12px" display="flex" justifyContent="space-between">
            <Button size="xs" variant="solid" bg="rgba(0,0,0,0.5)" color="white" _hover={{ bg: 'rgba(0,0,0,0.7)' }} onClick={() => navigate('/groups')}>Back</Button>
            <HStack spacing={1}>
              {isMember && canModerate && (
                <IconButton aria-label="Edit group" size="xs" isRound bg="rgba(0,0,0,0.5)" color="white" _hover={{ bg: 'rgba(0,0,0,0.7)' }} icon={<Pencil size={13} />} onClick={openEdit} />
              )}
              <Box color="white" bg="rgba(0,0,0,0.5)" borderRadius="full" p={1.5}>{group.isPublic ? <Globe size={13} /> : <Lock size={13} />}</Box>
            </HStack>
          </Box>
          <Box position="absolute" left="14px" right="14px" bottom="12px">
            <HStack justify="space-between" align="flex-end">
              <Box minW={0}>
                {group.isCreatorGroup && <Text fontSize="10px" color="#FFD700" fontWeight="800" letterSpacing="0.08em">CREATOR CREW</Text>}
                <Heading size="lg" color="white" noOfLines={1} textShadow="0 1px 8px rgba(0,0,0,0.8)">{group.name}</Heading>
                <Text fontSize="sm" color="rgba(255,255,255,0.85)" noOfLines={1}>
                  {group.memberCount} member{group.memberCount === 1 ? '' : 's'}{group.description ? ` · ${group.description}` : ''}
                </Text>
              </Box>
              {members.length > 0 && (
                <AvatarGroup size="sm" max={4} spacing="-10px" flexShrink={0}>
                  {members.slice(0, 6).map(m => <Avatar key={m.uid} name={m.name} src={m.photoURL || undefined} border="2px solid #0a1420" />)}
                </AvatarGroup>
              )}
            </HStack>
          </Box>
        </GroupCover>
        {isMember && (
          <HStack mt={3} mb={4} spacing={2}>
            <Button size="sm" colorScheme="cyan" leftIcon={<UserPlus size={15} />} onClick={openInvite} flex={1}>Invite people</Button>
            <Button size="sm" variant="outline" colorScheme="purple" leftIcon={<CalendarPlus size={15} />} onClick={openSchedule} flex={1}>Schedule a game</Button>
          </HStack>
        )}
        {!isMember && <Box h={4} />}

        {!isMember ? (
          <VStack align="stretch" spacing={4} mb={6}>
            {group.isPublic ? (
              <Button colorScheme="purple" isLoading={busy === 'join'} onClick={() => handleJoin()}>
                Join this group
              </Button>
            ) : group.requestPending ? (
              <Box bg={CARD_BG} border="1px solid rgba(139,92,246,0.35)" borderRadius="xl" p={4}>
                <Text color="white" fontWeight="700" fontSize="sm">Request sent</Text>
                <Text color="gray.400" fontSize="xs" mt={1}>The owner or a moderator will accept or decline it. You will get a notification either way.</Text>
                <Button size="xs" mt={3} variant="outline" colorScheme="red" onClick={handleCancelRequest}>Cancel request</Button>
              </Box>
            ) : (
              <Box bg={CARD_BG} border="1px solid rgba(148,163,184,0.2)" borderRadius="xl" p={4}>
                <Text color="white" fontWeight="700" fontSize="sm">This is a private group</Text>
                <Text color="gray.400" fontSize="xs" mt={1} mb={3}>Ask to join and the owner decides, or enter the invite code if you have one.</Text>
                <Button colorScheme="purple" size="sm" w="100%" isLoading={busy === 'join'} onClick={() => handleJoin()}>
                  Request to join
                </Button>
                <HStack mt={3}>
                  <Input size="sm" placeholder="Invite code" value={joinCodeInput} maxLength={6}
                    onChange={e => setJoinCodeInput(e.target.value.toUpperCase())} bg="gray.800" borderColor="gray.600" color="white"
                    onKeyDown={e => e.key === 'Enter' && joinCodeInput.trim() && handleJoin(joinCodeInput.trim())} />
                  <Button size="sm" variant="outline" colorScheme="cyan" isDisabled={!joinCodeInput.trim()} isLoading={busy === 'join'}
                    onClick={() => handleJoin(joinCodeInput.trim())}>Join with code</Button>
                </HStack>
              </Box>
            )}
            {members.length > 0 && (
              <Box>
                <Heading size="sm" color="gray.300" mb={2}>Members</Heading>
                <HStack spacing={-2}>
                  {members.slice(0, 12).map(m => <Avatar key={m.uid} size="sm" name={m.name} src={m.photoURL || undefined} border="2px solid #0a1420" />)}
                  {group.memberCount > 12 && <Text color="gray.500" fontSize="xs" pl={4}>+{group.memberCount - 12} more</Text>}
                </HStack>
              </Box>
            )}
          </VStack>
        ) : (
          <VStack align="stretch" spacing={4}>
            {/* Invite code (private groups) */}
            {group.joinCode && (
              <HStack bg={CARD_BG} border="1px solid rgba(148,163,184,0.2)" borderRadius="xl" px={4} py={3}>
                <Box flex={1}>
                  <Text color="gray.500" fontSize="xs">Private group code - anyone with it joins without a request</Text>
                  <Text color="#22d3ee" fontWeight="800" letterSpacing="2px">{group.joinCode}</Text>
                </Box>
                <Button size="xs" variant="outline" colorScheme="cyan" leftIcon={<Copy size={12} />} onClick={copyCode}>
                  {codeCopied ? 'Copied' : 'Copy'}
                </Button>
              </HStack>
            )}

            {/* Join requests (owner / moderators) */}
            {canModerate && pending.length > 0 && (
              <Box bg={CARD_BG} border="1px solid rgba(240,180,41,0.45)" borderRadius="xl" p={3}>
                <Text color="#f0b429" fontWeight="700" fontSize="sm" mb={2}>Join requests ({pending.length})</Text>
                <VStack align="stretch" spacing={2}>
                  {pending.map(p => (
                    <HStack key={p.uid}>
                      <Avatar size="xs" name={p.name} src={p.photoURL || undefined} />
                      <Text color="white" fontSize="sm" flex={1} noOfLines={1}>{p.name}</Text>
                      <IconButton aria-label="Accept" size="xs" colorScheme="green" icon={<Check size={14} />} isLoading={busy === `req-${p.uid}`} onClick={() => handleRequestAction(p.uid, 'accept')} />
                      <IconButton aria-label="Decline" size="xs" variant="outline" colorScheme="red" icon={<X size={14} />} isLoading={busy === `req-${p.uid}`} onClick={() => handleRequestAction(p.uid, 'decline')} />
                    </HStack>
                  ))}
                </VStack>
              </Box>
            )}

            {/* Tabs */}
            <HStack bg={CARD_BG} borderRadius="xl" p={1} spacing={1}>
              <TabButton id="chat" icon={<MessageCircle size={14} />} label="Chat" dot={!!group.chat?.unread && tab !== 'chat'} />
              <TabButton id="games" icon={<Calendar size={14} />} label="Games" dot={liveNow.length > 0 && tab !== 'games'} />
              <TabButton id="members" icon={<Users size={14} />} label="Members" />
            </HStack>

            {/* ── CHAT ── */}
            {tab === 'chat' && (
              <Box bg={CARD_BG} border="1px solid rgba(148,163,184,0.2)" borderRadius="xl" overflow="hidden">
                <Box ref={chatBoxRef} h={{ base: '52vh', md: '460px' }} overflowY="auto" px={3} py={3}>
                  {chatHasMore && (
                    <Box textAlign="center" mb={2}>
                      <Button size="xs" variant="ghost" color="gray.400" onClick={loadOlder}>Load earlier messages</Button>
                    </Box>
                  )}
                  {chatLoading && messages.length === 0 ? (
                    <Box textAlign="center" py={10}><Spinner color="cyan.400" size="sm" /></Box>
                  ) : messages.length === 0 ? (
                    <Text color="gray.500" fontSize="sm" textAlign="center" py={10}>No messages yet. Say hi to the crew.</Text>
                  ) : (
                    <VStack align="stretch" spacing={2}>
                      {messages.filter(m => !blockedUsers.has(m.senderId)).map(m => {
                        const mine = m.senderId === myUid;
                        const canDelete = !m.deleted && (mine || canModerate);
                        if (m.system || m.senderId === 'system') {
                          return (
                            <HStack key={m.id} justify="center" px={2}>
                              <Text color="gray.500" fontSize="xs" textAlign="center" bg="whiteAlpha.50" px={3} py={1} borderRadius="full" fontStyle="italic">
                                {m.deleted ? 'Notice removed' : m.text}
                              </Text>
                              {canModerate && !m.deleted && (
                                <IconButton aria-label="Remove notice" size="xs" variant="ghost" color="gray.600" minW="18px" h="18px" icon={<Trash2 size={11} />} onClick={() => handleDeleteMessage(m)} />
                              )}
                            </HStack>
                          );
                        }
                        return (
                          <HStack key={m.id} align="flex-start" spacing={2} flexDirection={mine ? 'row-reverse' : 'row'}>
                            {!mine && <Avatar size="xs" name={m.senderName} src={m.senderPhoto || undefined} mt={1} />}
                            <Box maxW="78%" bg={mine ? 'rgba(139,92,246,0.35)' : 'whiteAlpha.100'} borderRadius="lg" px={3} py={2}>
                              {!mine && <Text color="#a78bfa" fontSize="xs" fontWeight="700">{m.senderName}</Text>}
                              <Text color={m.deleted ? 'gray.600' : 'white'} fontSize="sm" fontStyle={m.deleted ? 'italic' : 'normal'} whiteSpace="pre-wrap" wordBreak="break-word">
                                {m.deleted ? 'Message deleted' : renderWithMentions(m.text)}
                              </Text>
                              <HStack justify="space-between" mt={0.5}>
                                <Text color="gray.500" fontSize="10px">{fmtTime(m.createdAt)}</Text>
                                <HStack spacing={0}>
                                  {canDelete && (
                                    <IconButton aria-label="Delete message" size="xs" variant="ghost" color="gray.500" minW="18px" h="18px" icon={<Trash2 size={11} />} onClick={() => handleDeleteMessage(m)} />
                                  )}
                                  {!mine && !m.deleted && (
                                    <Menu placement="bottom-end" isLazy>
                                      <MenuButton as={IconButton} aria-label="Message options" size="xs" variant="ghost" color="gray.500" minW="18px" h="18px" icon={<MoreHorizontal size={12} />} />
                                      <MenuList bg="#141c2b" borderColor="whiteAlpha.200" minW="160px">
                                        <MenuItem bg="transparent" _hover={{ bg: 'whiteAlpha.100' }} icon={<Flag size={13} />} onClick={() => reportMessage(m)}>Report message</MenuItem>
                                        <MenuItem bg="transparent" _hover={{ bg: 'whiteAlpha.100' }} icon={<Ban size={13} />} onClick={() => blockSender(m)}>Block {m.senderName}</MenuItem>
                                        {canModerate && <MenuItem bg="transparent" _hover={{ bg: 'whiteAlpha.100' }} icon={<UserMinus size={13} />} onClick={() => { const row = members.find(x => x.uid === m.senderId); if (row) handleMemberAction(row, 'remove'); }}>Remove from group</MenuItem>}
                                      </MenuList>
                                    </Menu>
                                  )}
                                </HStack>
                              </HStack>
                            </Box>
                          </HStack>
                        );
                      })}
                    </VStack>
                  )}
                </Box>
                <Box position="relative" borderTop="1px solid rgba(148,163,184,0.15)">
                  {mentionQuery !== null && mentionCandidates.length > 0 && (
                    <Box position="absolute" left="8px" right="8px" bottom="100%" mb={1} bg="#141c2b" border="1px solid rgba(139,92,246,0.5)" borderRadius="md" overflow="hidden" zIndex={5}>
                      {mentionCandidates.map(m => (
                        <HStack key={m.uid} px={3} py={2} spacing={2} cursor="pointer" _hover={{ bg: 'whiteAlpha.100' }} onMouseDown={e => { e.preventDefault(); pickMention(m.name); }}>
                          <Avatar size="xs" name={m.name} src={m.photoURL || undefined} />
                          <Text color="white" fontSize="sm">{m.name}</Text>
                          {m.role && m.role !== 'member' && <Badge colorScheme={m.role === 'owner' ? 'yellow' : 'purple'} fontSize="9px">{m.role.toUpperCase()}</Badge>}
                        </HStack>
                      ))}
                    </Box>
                  )}
                  <HStack p={2}>
                    <Input ref={chatInputRef} value={chatText} onChange={e => onChatInput(e.target.value)} maxLength={500}
                      placeholder="Message the group. Type @ to mention someone" bg="gray.800" borderColor="gray.600" color="white" size="sm"
                      onKeyDown={e => {
                        if (e.key === 'Tab' && mentionQuery !== null && mentionCandidates.length > 0) { e.preventDefault(); pickMention(mentionCandidates[0].name); return; }
                        if (e.key === 'Escape') { setMentionQuery(null); return; }
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          if (mentionQuery !== null && mentionCandidates.length > 0) { pickMention(mentionCandidates[0].name); return; }
                          handleSend();
                        }
                      }} />
                    <IconButton aria-label="Send" size="sm" colorScheme="purple" icon={<Send size={14} />} isLoading={sending} isDisabled={!chatText.trim()} onClick={handleSend} />
                  </HStack>
                </Box>
              </Box>
            )}

            {/* ── GAMES ── */}
            {tab === 'games' && (
              <VStack align="stretch" spacing={5}>
                <HStack justify="flex-end">
                  {group.isOwner
                    ? <Button size="xs" variant="ghost" colorScheme="red" onClick={handleDelete}>Delete group</Button>
                    : <Button size="xs" variant="ghost" colorScheme="red" onClick={handleLeave}>Leave group</Button>}
                </HStack>

                {liveNow.length > 0 && (
                  <Box>
                    <Heading size="sm" color="gray.300" mb={2}>Live now</Heading>
                    <VStack align="stretch" spacing={2}>
                      {liveNow.map(ev => (
                        <HStack key={ev.id} bg={CARD_BG} border="1px solid rgba(16,185,129,0.4)" borderRadius="xl" px={4} py={3}>
                          <Box flex={1} minW={0}>
                            <Text color="white" fontSize="sm" fontWeight="700" noOfLines={1}>
                              {ev.createdByName} started {ev.tableName || 'a game'}
                            </Text>
                            <Text color="gray.500" fontSize="xs" noOfLines={1}>
                              {ev.note || 'Tap join to grab a seat'}
                              {ev.privateCode ? ` - code ${ev.privateCode}` : ''}
                              {ev.invited?.includes(myUid) ? ' - you were invited' : ''}
                            </Text>
                          </Box>
                          <Button size="sm" colorScheme="green" leftIcon={<Play size={14} />}
                            onClick={() => navigate(`/game/${ev.tableId}`)}>
                            Join
                          </Button>
                        </HStack>
                      ))}
                    </VStack>
                  </Box>
                )}

                <Box>
                  <Heading size="sm" color="gray.300" mb={2}>Upcoming games</Heading>
                  {openScheduled.length === 0 ? (
                    <Text color="gray.500" fontSize="sm">
                      Nothing scheduled. Set a time, invite the whole group or just a few members,
                      and everyone who registers gets a reminder from PSS.
                    </Text>
                  ) : (
                    <VStack align="stretch" spacing={2}>
                      {openScheduled.map(ev => {
                        const registered = (ev.registered || []).includes(myUid);
                        const invited = (ev.invited || []).includes(myUid);
                        const canRun = ev.createdBy === myUid || canModerate;
                        return (
                          <Box key={ev.id} bg={CARD_BG} border={`1px solid ${invited && !registered ? 'rgba(34,211,238,0.6)' : 'rgba(139,92,246,0.35)'}`} borderRadius="xl" px={4} py={3}>
                            <Text color="white" fontSize="sm" fontWeight="700">
                              {ev.scheduledFor ? fmtWhen(ev.scheduledFor) : 'Soon'}
                              <Text as="span" color="gray.500" fontWeight="500"> - set by {ev.createdByName}</Text>
                            </Text>
                            {invited && !registered && <Text color="#22d3ee" fontSize="xs" mt={0.5}>You were invited to this one</Text>}
                            {ev.note && <Text color="gray.400" fontSize="xs" mt={0.5}>{ev.note}</Text>}
                            {ev.tableConfig && (
                              <Text color="gray.500" fontSize="xs" mt={0.5}>
                                {ev.tableConfig.smallBlind}/{ev.tableConfig.bigBlind}
                                {' '}{ev.tableConfig.currency === 'pkcash' ? 'PSS Coins' : 'PlayCoins'}
                                {' '}- {ev.tableConfig.maxSeats} seats - {(ev.registered || []).length} registered
                                {(ev.invited || []).length ? ` - ${ev.invited.length} invited` : ''}
                              </Text>
                            )}
                            <HStack mt={2} spacing={2} flexWrap="wrap">
                              <Button size="xs"
                                colorScheme={registered ? 'gray' : 'cyan'}
                                variant={registered ? 'outline' : 'solid'}
                                leftIcon={registered ? <BellOff size={12} /> : <Bell size={12} />}
                                isLoading={busy === ev.id}
                                onClick={() => handleRegister(ev, !registered)}>
                                {registered ? 'Registered - tap to drop' : 'Register'}
                              </Button>
                              {canRun && (
                                <Button size="xs" colorScheme="green" leftIcon={<Play size={12} />}
                                  isLoading={busy === ev.id}
                                  onClick={() => handleStartEvent(ev)}>
                                  Start now
                                </Button>
                              )}
                              {canRun && (
                                <Button size="xs" variant="ghost" colorScheme="red" onClick={() => handleCancelEvent(ev)}>
                                  Cancel
                                </Button>
                              )}
                            </HStack>
                          </Box>
                        );
                      })}
                    </VStack>
                  )}
                </Box>

                {history.length > 0 && (
                  <Box>
                    <Heading size="sm" color="gray.300" mb={2}>Recent</Heading>
                    <VStack align="stretch" spacing={1}>
                      {history.slice(0, 8).map(ev => (
                        <Text key={ev.id} color="gray.600" fontSize="xs">
                          {ev.type === 'scheduled' ? 'Scheduled game' : `${ev.createdByName} rallied the group`}
                          {ev.tableName ? ` - ${ev.tableName}` : ''} ({ev.status})
                        </Text>
                      ))}
                    </VStack>
                  </Box>
                )}
              </VStack>
            )}

            {/* ── MEMBERS ── */}
            {tab === 'members' && (
              <Box>
                <HStack justify="space-between" mb={2}>
                  <Heading size="sm" color="gray.300">Members ({group.memberCount})</Heading>
                  {!group.isOwner && <Button size="xs" variant="outline" colorScheme="red" onClick={handleLeave}>Leave group</Button>}
                </HStack>
                {group.isOwner && (
                  <Text color="gray.500" fontSize="xs" mb={3}>
                    Moderators can accept join requests, remove members and delete chat messages. You can have up to 10.
                  </Text>
                )}
                <VStack align="stretch" spacing={1}>
                  {members.map(m => {
                    const isMe = m.uid === myUid;
                    const canRemove = !isMe && m.role !== 'owner' && (group.isOwner || (group.isModerator && m.role !== 'moderator'));
                    return (
                      <HStack key={m.uid} bg={CARD_BG} borderRadius="lg" px={3} py={2} spacing={3}>
                        <Avatar size="sm" name={m.name} src={m.photoURL || undefined} cursor="pointer" onClick={() => navigate(`/profile/${m.uid}`)} />
                        <Box flex={1} minW={0}>
                          <HStack spacing={2}>
                            <Text color="white" fontSize="sm" fontWeight={m.role ? '700' : '500'} noOfLines={1}>{m.name}{isMe ? ' (you)' : ''}</Text>
                            {roleTag(m.role)}
                          </HStack>
                        </Box>
                        {group.isOwner && !isMe && m.role !== 'owner' && (
                          m.role === 'moderator'
                            ? <IconButton aria-label="Remove moderator" title="Remove moderator role" size="xs" variant="ghost" color="purple.300" icon={<ShieldOff size={14} />} isLoading={busy === `mem-${m.uid}`} onClick={() => handleMemberAction(m, 'demote')} />
                            : <IconButton aria-label="Make moderator" title="Make moderator" size="xs" variant="ghost" color="gray.400" icon={<Shield size={14} />} isLoading={busy === `mem-${m.uid}`} onClick={() => handleMemberAction(m, 'promote')} />
                        )}
                        {canRemove && (
                          <IconButton aria-label="Remove from group" title="Remove from group" size="xs" variant="ghost" color="red.300" icon={<UserMinus size={14} />} isLoading={busy === `mem-${m.uid}`} onClick={() => handleMemberAction(m, 'remove')} />
                        )}
                      </HStack>
                    );
                  })}
                </VStack>
                {membersNext !== null && (
                  <Box textAlign="center" mt={3}>
                    <Button size="xs" variant="outline" colorScheme="cyan" isLoading={loadingMore} onClick={loadMoreMembers}>
                      Show more members
                    </Button>
                  </Box>
                )}
              </Box>
            )}
          </VStack>
        )}
      </Container>

      {/* Invite modal */}
      <Modal isOpen={showInvite} onClose={() => setShowInvite(false)} isCentered scrollBehavior="inside">
        <ModalOverlay bg="blackAlpha.700" />
        <ModalContent bg="#1C1C1E" color="white" mx={4}>
          <ModalHeader fontSize="md">Invite people to {group.name}</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <VStack align="stretch" spacing={4}>
              <Box bg="gray.800" borderRadius="lg" p={3}>
                <Text fontSize="xs" color="gray.400" mb={2}>Share a link{group.joinCode ? '. It carries the private code, so friends join without a request.' : '. Anyone with it can join.'}</Text>
                <HStack>
                  <Input value={inviteLink} isReadOnly size="sm" bg="gray.900" borderColor="gray.600" fontSize="xs" />
                  <IconButton aria-label="Copy link" size="sm" colorScheme="cyan" variant="outline" icon={linkCopied ? <Check size={14} /> : <Link2 size={14} />} onClick={copyLink} />
                  <IconButton aria-label="Share" size="sm" colorScheme="cyan" icon={<Share2 size={14} />} onClick={shareLink} />
                </HStack>
              </Box>
              <Box>
                <Text fontSize="sm" mb={1} color="gray.400">Invite players directly</Text>
                <Input size="sm" placeholder="Search by username" value={inviteSearch} onChange={e => runInviteSearch(e.target.value)} bg="gray.800" borderColor="gray.600" mb={2} />
                <Box maxH="220px" overflowY="auto" border="1px solid rgba(148,163,184,0.2)" borderRadius="md" p={1}>
                  {(inviteSearch.trim().length >= 2 ? inviteResults : friends).length === 0 ? (
                    <Text color="gray.500" fontSize="xs" p={2}>
                      {friendsLoading ? 'Loading your friends...' : inviteSearch.trim().length >= 2 ? 'No players match.' : 'No friends yet. Search for a username above.'}
                    </Text>
                  ) : (inviteSearch.trim().length >= 2 ? inviteResults : friends).map(f => {
                    const already = members.some(m => m.uid === f.uid);
                    return (
                      <HStack key={f.uid} px={2} py={1.5} spacing={2} borderRadius="md" _hover={{ bg: 'whiteAlpha.100' }}>
                        <Avatar size="xs" name={f.name} src={f.photoURL || undefined} />
                        <Text color="white" fontSize="sm" flex={1} noOfLines={1}>{f.name}</Text>
                        {already ? <Text fontSize="xs" color="gray.500">In group</Text> : (
                          <Checkbox size="sm" colorScheme="cyan" isChecked={invitePicked.has(f.uid)} onChange={() => toggleInvitePick(f.uid)} />
                        )}
                      </HStack>
                    );
                  })}
                </Box>
                {inviteSearch.trim().length < 2 && friends.length > 0 && <Text fontSize="xs" color="gray.500" mt={1}>Showing your friends. Search to find anyone else.</Text>}
              </Box>
              <Button colorScheme="cyan" isDisabled={invitePicked.size === 0} isLoading={inviting} onClick={sendInvites} leftIcon={<UserPlus size={15} />}>
                Send {invitePicked.size || ''} invite{invitePicked.size === 1 ? '' : 's'}
              </Button>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Edit modal */}
      <Modal isOpen={showEdit} onClose={() => setShowEdit(false)} isCentered scrollBehavior="inside">
        <ModalOverlay bg="blackAlpha.700" />
        <ModalContent bg="#1C1C1E" color="white" mx={4}>
          <ModalHeader fontSize="md">Edit group</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <VStack align="stretch" spacing={4}>
              <GroupCover coverUrl={editCoverUrl} coverTheme={editTheme} name={editName || group.name} height={120}>
                <Box position="absolute" left="12px" bottom="10px">
                  <Text color="white" fontWeight="800" fontSize="lg" textShadow="0 1px 6px rgba(0,0,0,0.7)">{editName || group.name}</Text>
                </Box>
              </GroupCover>
              <Box>
                <Text fontSize="sm" mb={1} color="gray.400">Cover</Text>
                <HStack spacing={2} flexWrap="wrap">
                  {GROUP_COVER_THEMES.map((t, i) => (
                    <Box key={t.name} w="34px" h="34px" borderRadius="8px" cursor="pointer" title={t.name}
                      bg={`linear-gradient(135deg, ${t.from}, ${t.to})`}
                      border={!editCoverUrl && i === editTheme ? '2px solid #a78bfa' : '2px solid transparent'}
                      onClick={() => { setEditTheme(i); setEditCoverUrl(null); }} />
                  ))}
                  <Button as="label" size="sm" variant="outline" colorScheme="cyan" leftIcon={<ImagePlus size={14} />} isLoading={uploadingCover} cursor="pointer">
                    Upload photo
                    <input type="file" accept="image/*" hidden onChange={e => handleCoverFile(e.target.files?.[0] || null)} />
                  </Button>
                </HStack>
              </Box>
              {group.isOwner && (
                <Box>
                  <Text fontSize="sm" mb={1} color="gray.400">Name</Text>
                  <Input value={editName} onChange={e => setEditName(e.target.value)} maxLength={40} bg="gray.800" borderColor="gray.600" />
                </Box>
              )}
              <Box>
                <Text fontSize="sm" mb={1} color="gray.400">Description</Text>
                <Textarea value={editDesc} onChange={e => setEditDesc(e.target.value)} maxLength={200} bg="gray.800" borderColor="gray.600" rows={2} />
              </Box>
              <Button colorScheme="purple" isLoading={savingEdit} onClick={saveEdit}>Save</Button>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Schedule modal */}
      <Modal isOpen={showSchedule} onClose={() => setShowSchedule(false)} isCentered scrollBehavior="inside">
        <ModalOverlay bg="blackAlpha.700" />
        <ModalContent bg="#1C1C1E" color="white" mx={4}>
          <ModalHeader fontSize="md">Schedule a game</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <VStack align="stretch" spacing={4}>
              <Box>
                <Text fontSize="sm" mb={1} color="gray.400">When</Text>
                <Input type="datetime-local" value={schedWhen} onChange={e => setSchedWhen(e.target.value)}
                  bg="gray.800" borderColor="gray.600" sx={{ colorScheme: 'dark' }} />
              </Box>
              <Box>
                <Text fontSize="sm" mb={1} color="gray.400">Note (optional)</Text>
                <Textarea value={schedNote} onChange={e => setSchedNote(e.target.value)} maxLength={140}
                  placeholder="Friday night regular - bring your A game" bg="gray.800" borderColor="gray.600" rows={2} />
              </Box>
              <HStack spacing={3}>
                <Box flex={1}>
                  <Text fontSize="sm" mb={1} color="gray.400">Currency</Text>
                  <Select value={schedCurrency} onChange={e => setSchedCurrency(e.target.value as any)}
                    bg="gray.800" borderColor="gray.600" size="sm">
                    <option style={{ background: '#1A202C' }} value="playcoins">PlayCoins</option>
                    <option style={{ background: '#1A202C' }} value="pkcash">PSS Coins</option>
                  </Select>
                </Box>
                <Box w="90px">
                  <Text fontSize="sm" mb={1} color="gray.400">Seats</Text>
                  <Select value={schedSeats} onChange={e => setSchedSeats(Number(e.target.value))}
                    bg="gray.800" borderColor="gray.600" size="sm">
                    {[2, 4, 6, 8, 9, 10].map(n => <option style={{ background: '#1A202C' }} key={n} value={n}>{n}</option>)}
                  </Select>
                </Box>
              </HStack>
              <HStack spacing={3}>
                <Box flex={1}>
                  <Text fontSize="sm" mb={1} color="gray.400">Small blind</Text>
                  <Input type="number" value={schedSB} onChange={e => setSchedSB(Number(e.target.value) || 0)}
                    bg="gray.800" borderColor="gray.600" size="sm" />
                </Box>
                <Box flex={1}>
                  <Text fontSize="sm" mb={1} color="gray.400">Big blind</Text>
                  <Input type="number" value={schedBB} onChange={e => setSchedBB(Number(e.target.value) || 0)}
                    bg="gray.800" borderColor="gray.600" size="sm" />
                </Box>
              </HStack>

              {/* Who to invite */}
              <Box>
                <Text fontSize="sm" mb={1} color="gray.400">Who to invite</Text>
                <HStack spacing={2} mb={2}>
                  <Button size="xs" flex={1} variant={inviteEveryone ? 'solid' : 'outline'} colorScheme="purple" onClick={() => setInviteEveryone(true)}>Everyone in the group</Button>
                  <Button size="xs" flex={1} variant={!inviteEveryone ? 'solid' : 'outline'} colorScheme="purple" onClick={() => setInviteEveryone(false)}>Pick members{invitees.size ? ` (${invitees.size})` : ''}</Button>
                </HStack>
                {!inviteEveryone && (
                  <Box border="1px solid rgba(148,163,184,0.25)" borderRadius="md" p={2}>
                    <Input size="xs" placeholder="Search members" value={inviteFilter} onChange={e => setInviteFilter(e.target.value)} bg="gray.800" borderColor="gray.600" mb={2} />
                    <Box maxH="180px" overflowY="auto">
                      <VStack align="stretch" spacing={1}>
                        {pickable.length === 0 && <Text color="gray.500" fontSize="xs">No members match.</Text>}
                        {pickable.map(m => (
                          <Checkbox key={m.uid} size="sm" colorScheme="purple" isChecked={invitees.has(m.uid)} onChange={() => toggleInvitee(m.uid)}>
                            <HStack spacing={2}>
                              <Avatar size="2xs" name={m.name} src={m.photoURL || undefined} />
                              <Text fontSize="sm">{m.name}</Text>
                              {roleTag(m.role)}
                            </HStack>
                          </Checkbox>
                        ))}
                      </VStack>
                    </Box>
                    {membersNext !== null && (
                      <Button size="xs" variant="ghost" mt={1} isLoading={loadingMore} onClick={loadMoreMembers}>Load more members</Button>
                    )}
                  </Box>
                )}
              </Box>

              <Text fontSize="xs" color="gray.500">
                {inviteEveryone
                  ? 'Everyone in the group gets notified now.'
                  : 'Invited members get a notification and an invite in Messages; the game still shows in the group for everyone.'}
                {' '}Registered players get a reminder about 15 minutes before start, and again when you start the game.
              </Text>
              <Button colorScheme="purple" isLoading={scheduling} onClick={handleSchedule}>
                {inviteEveryone ? 'Schedule and notify the group' : `Schedule and invite ${invitees.size || ''}`.trim()}
              </Button>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default GroupDetailPage;
